import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import { spareDocumentsBySpareID } from "graphql/queries";
import { deleteSpareDocument } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import SpareParamatersTable from "components/spares/documents/SpareDocumentsTable";
import SpareDocumentCreateForm from "./SpareDocumentCreateForm";
import SpareDocumentUpdateForm from "./SpareDocumentUpdateForm";

const SpareDocuments = ({ spareID, spareName, visible, onClose }) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "spare:create-doc",
    "spare:update-doc",
    "spare:delete-doc",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [spareDocuments, setSpareDocuments] = useState([]);
  const [spareDocumentsLoading, setSpareDocumentsLoading] = useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    spareDocumentId: null,
    spareDocumentName: null,
  });
  const [modalSpareDocument, setModalSpareDocument] = useState({
    visible: false,
    spareDocumentId: null,
  });

  const columnsMapped = useMemo(
    () =>
      spareDocuments.map((item) => {
        return {
          ...item,
          actions: (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              <Button
                className="px-1"
                children={strings.actions.download}
                variation="link"
                size="small"
                onClick={() => downloadDoc(item.fileSrc)}
              />
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      spareDocumentId: item.id,
                      spareDocumentName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spareDocuments]
  );

  const onNext = () => {
    fetchSpareDocuments(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchSpareDocuments(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchSpareDocuments(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalSpareDocument({
      visible: true,
      action,
      spareDocumentId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalSpareDocument({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchSpareDocuments();
  };

  const downloadDoc = async (src) => {
    try {
      const res = await Storage.get(`spares/${src}`, {
        level: "public",
      });
      const link = document.createElement("a");
      link.href = res;
      link.setAttribute("download", "");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.log("Error Download Document", err);
      toast.error(strings.responses.errorDownload);
    }
  };

  async function fetchSpareDocuments(token, isNext = true, keywords) {
    try {
      setSpareDocumentsLoading(true);
      const limitMapped = 10;

      const spareDocumentsData = await API.graphql(
        graphqlOperation(spareDocumentsBySpareID, {
          spareID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      fileName: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      let spareDocumentsItems =
        spareDocumentsData.data.spareDocumentsBySpareID.items;

      setSpareDocuments(spareDocumentsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: spareDocumentsData.data.spareDocumentsBySpareID.nextToken,
        };
      });
      setSpareDocumentsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setSpareDocumentsLoading(false);
    } finally {
      setSpareDocumentsLoading(false);
    }
  }

  async function spareDocumentDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteSpareDocument, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const { id: deletedId, fileSrc: deletedSrc } =
        result.data.deleteSpareDocument;
      setSpareDocuments(spareDocuments.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        spareDocumentId: null,
      });

      if (deletedSrc)
        await Storage.remove(`spares/${deletedSrc}`, {
          level: "public",
        });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchSpareDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalSpareDocument.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {spareName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addSpareDocument}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {spareDocumentsLoading ? (
                  <Loader />
                ) : (
                  <SpareParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.spareDocumentName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    spareDocumentId: null,
                  })
                }
                onConfirm={() =>
                  spareDocumentDelete(modalDetails.spareDocumentId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalSpareDocument.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalSpareDocument.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalSpareDocument.action === "create" ? (
                <>
                  {strings.modalSpareDocument.addTitle} - {spareName}
                </>
              ) : (
                <>
                  {strings.modalSpareDocument.updateTitle} - {spareName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalSpareDocument.action === "create" ? (
                <SpareDocumentCreateForm
                  spareID={spareID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <SpareDocumentUpdateForm
                  spareDocumentId={modalSpareDocument.spareDocumentId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Documentos de Repuesto",
    successDeleted: "Documento de Repuesto eliminado con éxito",
    actions: {
      addSpareDocument: "Agregar Documento de Repuesto",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      download: "Descargar",
    },
    responses: {
      errorFetch: "Error al consultar documento de repuestos",
      errorDownload: "Error en la descarga",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el documento de repuesto {name}?",
    },
    modalSpareDocument: {
      addTitle: "Agregar Documento de Repuesto",
      updateTitle: "Actualizar Documento de Repuesto",
    },
    documentReturned: "Heredado del Tipo de Repuesto {name}",
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default SpareDocuments;
